import React from 'react'
import { Form, Input, Button, Typography } from 'antd'
import { Link, useNavigate } from 'react-router-dom'
import { observer } from 'mobx-react-lite'

import { login } from '../../../api/modules/api-user'
import { useStores } from '../../../cls/stores/MobXProvider'
import { clearErrors, startError } from '../../../stores/work-progress/actions'
import { runInAction, toJS } from 'mobx'
import { actSaveUser } from '../../../stores/users/actions'
import { getCookie } from '../../../config/utils'
import { GeneralContentWrapper } from '../../../components/Shared/GeneralComponents'
import { useSmartTranslation } from '../../../cls/TranslationContext'
const { Title, Text } = Typography
/**
 * Login
 *
 * React component that provides a login form with email and password fields.
 * Handles authentication via API, manages MobX user state, and redirects users after login.
 * Also prepares user data for cookie consent tracking (currently commented).
 *
 * @component
 * @returns {JSX.Element} Login form UI for user authentication.
 */
const Login = observer(({ actionOnLogin, hideRelated }) => {
	const { t } = useSmartTranslation()
	const navigate = useNavigate()
	const [passwordVisible, setPasswordVisible] = React.useState(false)
	const { users } = useStores()

	// Updates the user's cookie consent preference based on the stored cookie value.
	// Todo: We need refactor for user cookie consent when login
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	/**
	 * updateUserCookieConsent
	 *
	 * Updates the user's cookie consent flag in their profile based on browser cookie.
	 * Currently unused but planned for managing compliance with cookie consent.
	 *
	 * @param {object} user - The user object to update.
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const updateUserCookieConsent = async (user) => {
		const consent = getCookie('cookieConsent')
		if (consent !== null) {
			runInAction(() => {
				user.setCookieConsent(consent)
			})

			actSaveUser(user.user_id, toJS(user.full), toJS(user.allowed)).then(
				() => {
					users.saveState()
				}
			)
		}
	}

	/**
	 * onFinish
	 *
	 * Handles form submission for user login.
	 * Calls the login API, stores user info and API token, and navigates accordingly.
	 *
	 * @param {object} values - The login form values, including email and password.
	 */
	const onFinish = async (values) => {
		try {
			login(values)
				.then((response) => {
					if (response.status === 'success') {
						const userId = response.user.user_id
						users.setLoggedInUser(userId, response.apiToken)
						const user = users.requireUserById(userId)
						user.updateFromServer(response.user)
						users.saveState()
						clearErrors()

						// Update user cookie consent preference
						// updateUserCookieConsent(user)
						// Prefer callback when provided (modal flow)

						if (typeof actionOnLogin === 'function') {
							actionOnLogin()
							return
						}

						// Fallback: classic redirect flow
						if (users.redirectOnLogin) {
							const url = users.redirectOnLogin
							users.clearRedirectOnLogin()
							navigate(url)
						} else {
							navigate('/dashboard')
						}
					} else {
						startError(response.message)
					}
				})
				.catch((error) => {
					startError(error.message)
					console.error('Failed to fetch user info:', error)
				})
		} catch (error) {
			startError(t('__x-try-again%single.login.label'))
			console.error('Login failed:', error)
		}
	}

	return (
		<GeneralContentWrapper>
			<Title level={2}>{t('single.welcome-back')}</Title>
			<p>{t('single.login.enter-email-password')}</p>

			<Form
				name="login_form"
				initialValues={{ remember: true }}
				onFinish={onFinish}
				layout="vertical"
			>
				<Form.Item
					name="email"
					label={t('single.email')}
					rules={[
						{
							required: true,
							message: t('__provide.please%single.email')
						}
					]}
				>
					<Input placeholder={t('single.email')} />
				</Form.Item>

				<Form.Item
					name="password"
					label={t('single.password.label')}
					rules={[
						{
							required: true,
							message: t('__provide.please%single.password.label')
						}
					]}
				>
					<Input.Password
						placeholder={t('single.password.label')}
						visibilityToggle={{
							visible: passwordVisible,
							onVisibleChange: setPasswordVisible
						}}
					/>
				</Form.Item>

				<Form.Item>
					<Button
						type="primary"
						htmlType="submit"
						style={{ width: '100%' }}
					>
						{t('single.login.label')}
					</Button>
				</Form.Item>

				{!hideRelated && (
					<Form.Item>
						<Text style={{ display: 'block', textAlign: 'center' }}>
							{t('single.password.forgot')}{' '}
							<Link to="../reset-password">
								{t('single.password.reset')}
							</Link>
						</Text>
						<Text style={{ display: 'block', textAlign: 'center' }}>
							{t('single.account.missing')}{' '}
							<Link to="../signup">{t('single.signup.now')}</Link>
						</Text>
					</Form.Item>
				)}
			</Form>
		</GeneralContentWrapper>
	)
})

export default Login
